import React from 'react';
import {
  TitleAndSubtitle,
  ContentButtonsWrapper,
} from '../../layout/layout-container.styled';
import { Text, font } from '../../../style-utils/text';
import { StyledButton } from '../../styled-button/styled-button';
import { CheckRing } from '../../icons/check-ring';
import { GUEST_TYPES } from '../../../constants/visitor-types';
import { DefaultComponentProps } from '../../app-router/component-props';
import { VisitorLogState } from '../../hooks/use-visitor-log-handler';
import { toKebabCase } from '../../app-router/path-generator';
import { VISITOR_TYPES } from '../../layout/visitor-log/navbar-controller';
import { useOptionAdvance } from '../../hooks/use-option-advance';

/**
 * Visitor-type picker — step 1 of the visitor-log wizard. Renders ONLY pane
 * content: the persistent WizardLayout shell hosts it via <Outlet/>, so
 * Continue into the first step swaps just the pane (no shell remount).
 */
export const VisitorLog: React.FC<DefaultComponentProps> = ({
  navigate,
  finish,
  state,
  changeHandler,
  allPaths,
}) => {
  const { visitorType } = state as VisitorLogState;
  const allRoutes = allPaths || [];

  const { pendingValue, selectAndAdvance } = useOptionAdvance({
  /**
   * A NEW ENTRY STARTS WHEN A TYPE IS PICKED — NOT WHEN THIS SCREEN MOUNTS.
   *
   * This used to be `useEffect(() => { finish(); }, [])`, and `finish` replaces
   * the whole state with initialState. The picker is inside the persistent
   * wizard shell, so THREE one-tap routes reach this mount from inside a
   * part-filled entry — the shell's own "Visitor Log" rail item, a Back out of
   * step 1, and the sidebar. Any of them silently destroyed everything typed so
   * far: no confirmation, no undo, no sign that anything had happened.
   *
   * Clearing on SELECT is the same guarantee with none of the loss: picking a
   * type is the explicit act of starting a new entry, and it is the only way
   * into the steps. Arriving at the picker and leaving again now costs nothing.
   *
   * The two calls compose safely — both setters use functional updaters, so the
   * reset lands first and the type is written on top of it.
   */
    onSelect: value => {
      finish();
      changeHandler(value, 'visitorType', value);
    },
    onAdvance: value => {
      const basePath =
        value === VISITOR_TYPES.EMERGENCY
          ? '/emergency'
          : `/${toKebabCase(value)}`;
      const firstMatchingPath = allRoutes.find(path =>
        path.startsWith(basePath)
      );

      navigate(
        firstMatchingPath ? `/visitor-log${firstMatchingPath}` : '/main-menu',
        { replace: false }
      );
    },
  });

  return (
    <>
      {/* THE BACK BUTTON, TOP LEFT — the product's one design and one
          position (components/layout/back-button.tsx). It replaced a
          bottom-left <NavButton direction="back">, which was the wizard's
          STEP control being used for a PAGE exit: leaving the flow and
          stepping back inside it looked identical and sat in the same corner. */}
      {/* The BackBar that used to sit here is now rendered once by
          WizardLayout, inside StepPane (WP-10). Three routes each carried a
          copy and every step past the first had none, which is both the missing
          exit (BUG-011) and the ~60px title jump on entering a step
          (BUG-019). */}
      <TitleAndSubtitle direction="column">
        <Text as="h1" fontSize={font.size.xl} fontWeight={700} mt={0} mb={8}>
          Visitor Type
        </Text>
        <Text color={font.color.gray} fontSize={font.size.xs} mt={0} mb={0}>
          Please select one of the options below
        </Text>
      </TitleAndSubtitle>
      <ContentButtonsWrapper>
        {/* NO RESIDENT TILE HERE. Owner instruction, 2026-08-11: "one source of
            truth, which should be the main menu."
            Resident in / out is a MAIN MENU card (RESIDENT_IN_PATH /
            RESIDENT_OUT_PATH in main-menu-model.ts) and it never belonged to
            this wizard: it navigates straight to a single screen rather than
            entering the five-step flow, so a tile here looked like a sixth
            visitor type and behaved like an exit. Two doors onto one screen is
            also two places to keep a permission gate in step.
            The route (/visitor-log/resident) is unchanged — only this duplicate
            entrance to it is gone. */}
        <StyledButton
          onClick={() => selectAndAdvance(GUEST_TYPES.GUEST)}
          text={GUEST_TYPES.GUEST}
          icon={<CheckRing />}
          isSelected={visitorType === GUEST_TYPES.GUEST}
          pending={pendingValue === GUEST_TYPES.GUEST}
        />
        <StyledButton
          onClick={() => selectAndAdvance(GUEST_TYPES.PROSPECT)}
          text={GUEST_TYPES.PROSPECT}
          icon={<CheckRing />}
          isSelected={visitorType === GUEST_TYPES.PROSPECT}
          pending={pendingValue === GUEST_TYPES.PROSPECT}
        />
        <StyledButton
          onClick={() => selectAndAdvance(GUEST_TYPES.VENDOR)}
          text={GUEST_TYPES.VENDOR}
          icon={<CheckRing />}
          isSelected={visitorType === GUEST_TYPES.VENDOR}
          pending={pendingValue === GUEST_TYPES.VENDOR}
        />
        <StyledButton
          onClick={() => selectAndAdvance(GUEST_TYPES.DELIVERY)}
          text={GUEST_TYPES.DELIVERY}
          icon={<CheckRing />}
          isSelected={visitorType === GUEST_TYPES.DELIVERY}
          pending={pendingValue === GUEST_TYPES.DELIVERY}
        />
        <StyledButton
          onClick={() => selectAndAdvance(GUEST_TYPES.EMERGENCY)}
          text={GUEST_TYPES.EMERGENCY}
          icon={<CheckRing />}
          isSelected={visitorType === GUEST_TYPES.EMERGENCY}
          pending={pendingValue === GUEST_TYPES.EMERGENCY}
        />
      </ContentButtonsWrapper>
    </>
  );
};
